<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <base-alert-dialog
      :show="alertDialog.show"
      :variant="alertDialog.variant"
      :title="alertDialog.title"
      :message="alertDialog.message"
      @confirm="alertDialog.show = false"
      @cancel="alertDialog.show = false"
    />

    <base-alert-dialog
      :show="confirmDialog.show"
      variant="warning"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      show-cancel
      :confirm-text="confirmDialog.confirmText"
      confirm-variant="danger"
      @confirm="resolveConfirm(true)"
      @cancel="resolveConfirm(false)"
    />

    <!--    Breadcrumbs + Watchers row-->
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/projects">Projects</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <a :href="'/projects/'+ ticket.project_id">{{ ticket.project_name }}</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{ ticket.ticket_key }}</span>
      </breadcrumb-item>
    </breadcrumb>

    <div class="row">
      <!--  MAIN CONTENT  -->
      <div class="col-lg-8">
        <!--  Parent ticket banner  -->
        <div v-if="parentTicket" class="parent-banner">
          <i class="tim-icons el-icon-top"></i>
          <span class="parent-label">Parent:</span>
          <a :href="'/projects/' + projectId + '/tickets/' + parentTicket.id"
             class="parent-link">
            {{ parentTicket.ticket_key }} - {{ parentTicket.title }}
          </a>
        </div>

        <ticket-card :ticket-key="ticket.ticket_key">
          <!--  HEADER: click-to-edit title  -->
          <template #title>
            <div v-if="!editingTitle" class="ticket-title-display" @click="startEditTitle">
              <h4 class="card-title mb-0">{{ title }}</h4>
            </div>
            <div v-else>
              <input
                v-model="title"
                ref="titleInput"
                class="form-control ticket-title-input"
                placeholder="Ticket title"
                @blur="blurTitle"
                @keydown.enter="$refs.titleInput.blur()"
                @keydown.esc="cancelEditTitle"
              />
            </div>
            <small v-if="errs.summary" class="text-danger">{{ errs.summary }}</small>
          </template>

          <!--  DESCRIPTION  -->
          <ticket-description
            ref="description"
            :value="savedDescriptionContent"
            editable
            :saving="savingDescription"
            :dark="isDarkMode"
            @save="saveDescription"
          />

          <tab-rail
            :tabs="tabRailTabs"
            :value="activeTabIndex"
            @input="switchTab($event)"
            class="mb-3"
          ></tab-rail>

          <!--  COMMENTS TAB  -->
          <div v-show="activeTabName === 'comments'">
            <ticket-comments
              ref="ticketComments"
              :comments="comments"
              :can-comment="hasPermissions('project_comment')"
              :saving="savingComment"
              :deleting-id="deletingCommentId"
              :dark="isDarkMode"
              @create="createComment"
              @update="updateComment"
              @delete="deleteComment"
              @checklist-change="handleCommentCheckbox"
            />
          </div>

          <!--  SUBTASKS TAB  -->
          <div v-show="activeTabName === 'subtasks'" v-if="!parentTicket">
            <!--  Progress bar  -->
            <div v-if="subtasks.length > 0" class="subtask-progress">
              <div class="subtask-progress-header">
                <span class="subtask-progress-text">{{ completedSubtaskCount }}/{{ subtasks.length }} completed</span>
              </div>
              <div class="subtask-progress-track">
                <div class="subtask-progress-fill" :style="{ width: subtaskCompletionPercent + '%' }"></div>
              </div>
            </div>

            <!--  Empty state  -->
            <div v-if="subtasks.length === 0 && !showCreateSubtaskForm" class="empty-state">
              <i class="tim-icons icon-bullet-list-67"></i>
              <p>Break this ticket into smaller tasks</p>
            </div>

            <!--  ARIA live region for reorder announcements  -->
            <div aria-live="assertive" class="sr-only">{{ reorderAnnouncement }}</div>

            <!--  Subtask rows  -->
            <draggable v-model="subtasks"
                       handle=".subtask-drag-handle"
                       ghost-class="subtask-ghost"
                       chosen-class="subtask-chosen"
                       :animation="150"
                       :force-fallback="true"
                       :disabled="!!editingSubtaskId"
                       @end="onSubtaskDragEnd">
            <div v-for="(subtask, subtaskIndex) in subtasks" :key="'ST' + subtask.id"
                 v-show="!editingSubtaskId || editingSubtaskId === subtask.id">

              <!--  Edit mode  -->
              <div v-if="editingSubtaskId === subtask.id" style="padding: 8px 10px;">
                <base-input label="Title" style="margin-bottom: 8px;">
                  <div style="position: relative;">
                    <input v-model="editSubtask.title"
                           ref="editSubtaskTitle"
                           class="form-control"
                           placeholder="Subtask title"
                           @keydown.enter.prevent="saveEditSubtask(subtask)"
                           @keydown.esc="cancelEditSubtask" />
                    <span class="enter-hint">
                      Enter &#x23CE;
                    </span>
                  </div>
                </base-input>
                <div class="d-none d-sm-flex" style="gap: 8px;">
                  <base-input label="Assignee" style="flex: 1; margin-bottom: 0;">
                    <el-select v-model="editSubtask.assignee" placeholder="Assignee"
                               class="select-primary subtask-inline-select" clearable filterable autocomplete="off">
                      <el-option v-for="u in users" :key="u.id" class="select-primary" :label="u.name" :value="u.id"></el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Priority" style="flex: 1; margin-bottom: 0;">
                    <el-select v-model="editSubtask.priority" placeholder="Priority"
                               class="select-primary subtask-inline-select">
                      <el-option v-for="p in priorities" :key="p.id" class="select-primary" :label="p.name" :value="p.id">
                        <i :class="p.icon" :style="{ color: p.color, marginRight: '6px' }"></i>{{ p.name }}
                      </el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Due date" style="flex: 1; margin-bottom: 0;">
                    <el-date-picker v-model="editSubtask.due_date" type="date"
                      placeholder="No due date" :format="day_of_week + dateFormatCal"
                      value-format="yyyy-MM-dd" clearable style="width: 100%">
                    </el-date-picker>
                  </base-input>
                </div>
                <div class="d-flex d-sm-none flex-wrap" style="gap: 8px;">
                  <base-input label="Assignee" style="flex: 1; min-width: 120px; margin-bottom: 0;">
                    <el-select v-model="editSubtask.assignee" placeholder="Assignee"
                               class="select-primary subtask-inline-select" clearable filterable autocomplete="off">
                      <el-option v-for="u in users" :key="u.id" class="select-primary" :label="u.name" :value="u.id"></el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Priority" style="flex: 1; min-width: 120px; margin-bottom: 0;">
                    <el-select v-model="editSubtask.priority" placeholder="Priority"
                               class="select-primary subtask-inline-select">
                      <el-option v-for="p in priorities" :key="p.id" class="select-primary" :label="p.name" :value="p.id">
                        <i :class="p.icon" :style="{ color: p.color, marginRight: '6px' }"></i>{{ p.name }}
                      </el-option>
                    </el-select>
                  </base-input>
                  <base-input label="Due date" style="flex: 1; min-width: 120px; margin-bottom: 0;">
                    <el-date-picker v-model="editSubtask.due_date" type="date"
                      placeholder="No due date" :format="day_of_week + dateFormatCal"
                      value-format="yyyy-MM-dd" clearable style="width: 100%">
                    </el-date-picker>
                  </base-input>
                </div>
                <div class="subtask-form-actions mt-2">
                  <base-button size="sm" type="default" @click="cancelEditSubtask">Cancel</base-button>
                  <base-button class="ml-2" size="sm" type="primary" @click="saveEditSubtask(subtask)"
                               :disabled="!editSubtask.title.trim()">Save</base-button>
                </div>
              </div>

              <!--  View mode (desktop)  -->
              <div v-else
                   class="subtask-row d-none d-sm-flex align-items-center"
                   style="padding: 12px 0.75rem; gap: 12px;">

                <i class="el-icon-rank subtask-drag-handle"
                   ref="dragHandles"
                   tabindex="0"
                   role="button"
                   :aria-label="'Reorder subtask ' + subtask.ticket_key"
                   aria-roledescription="sortable"
                   @keydown="handleSubtaskKeydown($event, subtaskIndex)"
                   @click.stop></i>

                <input type="checkbox"
                  class="subtask-cb"
                  :checked="isSubtaskCompleted(subtask)"
                  @change="toggleSubtaskComplete(subtask)" />

                <span class="subtask-key">
                  {{ subtask.ticket_key }}
                </span>

                <a class="text-white subtask-title subtask-link"
                   :href="'/projects/' + projectId + '/tickets/' + subtask.id"
                   target="_blank"
                   @click.stop
                   style="font-size: 14px; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                   :style="{ textDecoration: isSubtaskCompleted(subtask) ? 'line-through' : 'none',
                             opacity: isSubtaskCompleted(subtask) ? 0.5 : 1 }">
                  {{ subtask.title }}
                </a>

                <div v-if="subtask.assignee_name"
                     class="d-flex align-items-center justify-content-center"
                     style="width: 26px; height: 26px; background-color: #1d8cf8; border-radius: 50%;
                            font-size: 10px; font-weight: bold; color: white; flex-shrink: 0;"
                     :title="subtask.assignee_name">
                  {{ getInitials(subtask.assignee_first_name, subtask.assignee_last_name) || (subtask.assignee_name ? subtask.assignee_name.charAt(0).toUpperCase() : '') }}
                </div>
                <div v-else style="width: 26px; flex-shrink: 0;"></div>

                <span v-if="subtask.due_date" style="font-size: 12px; white-space: nowrap; flex-shrink: 0;"
                      class="subtask-due-date" :class="{ 'subtask-overdue': isSubtaskOverdue(subtask) }">
                  <i class="el-icon-date" style="margin-right: 2px;"></i>{{ formatSubtaskDueDate(subtask.due_date) }}
                </span>

                <i v-if="getPriority(subtask.priority)"
                   :class="getPriority(subtask.priority).icon"
                   :style="{ color: getPriority(subtask.priority).color, fontSize: '14px', flexShrink: 0 }"
                   :title="getPriority(subtask.priority).name"></i>

                <div class="subtask-actions" @click.stop style="display: flex; gap: 8px; flex-shrink: 0;">
                  <i class="tim-icons icon-pencil text-white"
                     @click="startEditSubtask(subtask)"
                     style="font-size: 14px; cursor: pointer;"
                     title="Edit"></i>
                  <i class="tim-icons icon-simple-remove text-white"
                     @click="deleteSubtask(subtask)"
                     style="font-size: 14px; cursor: pointer;"
                     title="Delete"></i>
                </div>

              <!--  View mode (mobile)  -->
              </div><div v-if="editingSubtaskId !== subtask.id"
                   class="subtask-row d-block d-sm-none"
                   style="padding: 10px 0.75rem;">
                <div class="d-flex align-items-center" style="gap: 8px;">
                  <i class="el-icon-rank subtask-drag-handle subtask-drag-handle--mobile"
                     @click.stop></i>

                  <input type="checkbox"
                    class="subtask-cb"
                    :checked="isSubtaskCompleted(subtask)"
                    @change="toggleSubtaskComplete(subtask)" />
                  <span class="subtask-key" style="font-size: 12px;">
                    {{ subtask.ticket_key }}
                  </span>
                  <a class="text-white subtask-title subtask-link"
                     :href="'/projects/' + projectId + '/tickets/' + subtask.id"
                     target="_blank"
                   @click.stop
                     style="font-size: 14px; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                     :style="{ textDecoration: isSubtaskCompleted(subtask) ? 'line-through' : 'none',
                               opacity: isSubtaskCompleted(subtask) ? 0.5 : 1 }">
                    {{ subtask.title }}
                  </a>
                </div>
                <div class="d-flex align-items-center mt-1" style="gap: 10px; padding-left: 44px;">
                  <div v-if="subtask.assignee_name"
                       class="d-flex align-items-center justify-content-center"
                       style="width: 22px; height: 22px; background-color: #1d8cf8; border-radius: 50%;
                              font-size: 9px; font-weight: bold; color: white; flex-shrink: 0;"
                       :title="subtask.assignee_name">
                    {{ getInitials(subtask.assignee_first_name, subtask.assignee_last_name) || (subtask.assignee_name ? subtask.assignee_name.charAt(0).toUpperCase() : '') }}
                  </div>
                  <span v-if="subtask.due_date" style="font-size: 11px; white-space: nowrap;"
                        class="subtask-due-date" :class="{ 'subtask-overdue': isSubtaskOverdue(subtask) }">
                    <i class="el-icon-date" style="margin-right: 2px;"></i>{{ formatSubtaskDueDate(subtask.due_date) }}
                  </span>
                  <i v-if="getPriority(subtask.priority)"
                     :class="getPriority(subtask.priority).icon"
                     :style="{ color: getPriority(subtask.priority).color, fontSize: '12px', flexShrink: 0 }"
                     :title="getPriority(subtask.priority).name"></i>
                  <div style="margin-left: auto; display: flex; gap: 8px; flex-shrink: 0;">
                    <i class="tim-icons icon-pencil text-white"
                       @click="startEditSubtask(subtask)"
                       style="font-size: 13px; cursor: pointer;"
                       title="Edit"></i>
                    <i class="tim-icons icon-simple-remove text-white"
                       @click="deleteSubtask(subtask)"
                       style="font-size: 13px; cursor: pointer;"
                       title="Delete"></i>
                  </div>
                </div>
              </div>
            </div>
            </draggable>

            <!--  Create subtask form  -->
            <div v-if="showCreateSubtaskForm" style="margin-top: 8px;">
              <base-input label="Title" style="margin-bottom: 8px;">
                <div style="position: relative;">
                  <input v-model="newSubtask.title"
                         ref="createSubtaskTitle"
                         class="form-control"
                         placeholder="Subtask title"
                         @keydown.enter.prevent="createSubtask"
                         @keydown.esc="cancelCreateSubtask" />
                  <span class="enter-hint">Enter &#x23CE;</span>
                </div>
              </base-input>
              <div class="d-none d-sm-flex" style="gap: 8px;">
                <base-input label="Assignee" style="flex: 1; margin-bottom: 0;">
                  <el-select v-model="newSubtask.assignee" placeholder="Assignee"
                             class="select-primary subtask-inline-select" clearable autocomplete="off">
                    <el-option v-for="u in users" :key="u.id" class="select-primary" :label="u.name" :value="u.id"></el-option>
                  </el-select>
                </base-input>
                <base-input label="Priority" style="flex: 1; margin-bottom: 0;">
                  <el-select v-model="newSubtask.priority" placeholder="Priority"
                             class="select-primary subtask-inline-select">
                    <el-option v-for="p in priorities" :key="p.id" class="select-primary" :label="p.name" :value="p.id">
                      <i :class="p.icon" :style="{ color: p.color, marginRight: '6px' }"></i>{{ p.name }}
                    </el-option>
                  </el-select>
                </base-input>
                <base-input label="Due date" style="flex: 1; margin-bottom: 0;">
                  <el-date-picker v-model="newSubtask.due_date" type="date"
                    placeholder="No due date" :format="day_of_week + dateFormatCal"
                    value-format="yyyy-MM-dd" clearable style="width: 100%">
                  </el-date-picker>
                </base-input>
              </div>
              <div class="d-flex d-sm-none flex-wrap" style="gap: 8px;">
                <base-input label="Assignee" style="flex: 1; min-width: 120px; margin-bottom: 0;">
                  <el-select v-model="newSubtask.assignee" placeholder="Assignee"
                             class="select-primary subtask-inline-select" clearable autocomplete="off">
                    <el-option v-for="u in users" :key="u.id" class="select-primary" :label="u.name" :value="u.id"></el-option>
                  </el-select>
                </base-input>
                <base-input label="Priority" style="flex: 1; min-width: 120px; margin-bottom: 0;">
                  <el-select v-model="newSubtask.priority" placeholder="Priority"
                             class="select-primary subtask-inline-select">
                    <el-option v-for="p in priorities" :key="p.id" class="select-primary" :label="p.name" :value="p.id">
                      <i :class="p.icon" :style="{ color: p.color, marginRight: '6px' }"></i>{{ p.name }}
                    </el-option>
                  </el-select>
                </base-input>
                <base-input label="Due date" style="flex: 1; min-width: 120px; margin-bottom: 0;">
                  <el-date-picker v-model="newSubtask.due_date" type="date"
                    placeholder="No due date" :format="day_of_week + dateFormatCal"
                    value-format="yyyy-MM-dd" clearable style="width: 100%">
                  </el-date-picker>
                </base-input>
              </div>
              <div class="subtask-form-actions mt-2">
                <base-button size="sm" type="default" @click="cancelCreateSubtask">Cancel</base-button>
                <base-button class="ml-2" size="sm" type="primary" @click="createSubtask"
                             :disabled="!newSubtask.title.trim()">Save</base-button>
              </div>
            </div>

            <!--  Create button  -->
            <div v-if="!showCreateSubtaskForm && !editingSubtaskId" style="padding: 8px 0;">
              <base-button size="sm" type="primary" @click="openCreateSubtaskForm">
                <i class="tim-icons icon-simple-add mr-1"></i> Create subtask
              </base-button>
            </div>
          </div>

          <!--  TIME TRACKING TAB  -->
          <div v-if="activeTabName === 'time_tracking' && (hasPermissions('time_log_create') || hasPermissions('time_log_view'))">
            <div class="row mb-2">
              <div class="col">
                <label class="section-label">Ticket progress</label>
                <el-progress class="custom-progress-bar"
                             :class="{
                            'under-estimate': !overEstimate,
                            'over-estimate': overEstimate
                          }"
                             :percentage="spentPercent" ></el-progress>
                <label v-if="!overEstimate" class="text-white" style="font-size: 13px;">{{formatMinutesToTimeString(ticket.time_estimate_minutes - totalTimeSpent)}} remaining</label>
                <label v-if="overEstimate" class="text-white" style="font-size: 13px;">{{formatMinutesToTimeString(totalTimeSpent - ticket.time_estimate_minutes)}} over original estimate</label>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <label class="section-label">Original estimate</label>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <base-button style="font-weight: bold; padding: 0px 0px 0px 0px" link @click.prevent="editTimeEstimate" class="card-title">
                  {{formatMinutesToTimeString(ticket.time_estimate_minutes)}} <i style="transform: scale(0.8)" class="tim-icons icon-pencil ml-2"></i>
                </base-button>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <label class="section-label">Total time</label>
              </div>
            </div>
            <div class="row">
              <div class="col">
              <span class="card-title" style="font-weight: bold;">
                {{formatMinutesToTimeString(totalTimeSpent)}}
              </span>
              </div>
            </div>

            <!-- Time Logs Section -->
            <div class="row mt-4">
              <div class="col">
                <label class="section-label mb-3">Time logs</label>

                <div v-if="time_logs.length === 0" class="empty-state">
                  <i class="tim-icons icon-watch-time"></i>
                  <p>No time logs recorded yet.</p>
                </div>
                <div class="d-none d-sm-block" v-if="time_logs.length > 0">
                  <div class="row">
                    <div class="col">
                    <b-table striped hover :items="time_logs"
                             :fields="['start_time', 'end_time', 'duration', 'category', 'user', 'notes', 'actions']">
                      <template v-slot:cell(category)="row">
                        <badge v-if="row.item.category_name" :style="{'background-color': row.item.category_color || '#1d8cf8'}">
                          {{ row.item.category_name }}
                        </badge>
                        <span v-else class="table-muted">Uncategorized</span>
                      </template>
                      <template v-slot:cell(start_time)="row">
                        {{ getLocalDateTime(row.item.start_time) }}
                      </template>
                      <template v-slot:cell(end_time)="row">
                    <span v-if="row.item.end_time">
                            {{ getLocalDateTime(row.item.end_time) }}
                          </span>
                        <span v-else class="table-muted">-</span>
                      </template>
                      <template v-slot:cell(duration)="row">
                    <span class="font-weight-bold">
                            {{ formatMinutesToTimeString(row.item.duration_minutes || 0) }}
                          </span>
                      </template>

                      <template v-slot:cell(user)="row">
                        {{ row.item.user_name }}
                      </template>
                      <template v-slot:cell(notes)="row">
                    <span v-if="row.item.notes" :title="row.item.notes">
                            {{ row.item.notes.substring(0, 30) }}{{ row.item.notes.length > 30 ? '...' : '' }}
                          </span>
                        <span v-else class="table-muted">-</span>
                      </template>
                      <template v-slot:cell(actions)="row">
                        <el-dropdown v-if="!row.item.is_running && (hasPermissions('time_log_update') || hasPermissions('time_log_delete'))">
                          <base-button
                            link
                            type="primary"
                            size="sm"
                          > <i style="font-size: large" class="tim-icons el-icon-more"></i>
                          </base-button>
                          <template #dropdown>
                            <el-dropdown-menu>
                              <a class="dropdown-item mt-1 mb-2" v-if="hasPermissions('time_log_update')" @click.prevent="editTimeLogPrompt(row.item)" href="#">
                                <i class="tim-icons text-warning icon-pencil mr-2"></i> Update time log
                              </a>
                              <hr v-if="hasPermissions('time_log_delete')">
                              <a class="dropdown-item mt-1 mb-2" v-if="hasPermissions('time_log_delete')" @click.prevent="archiveTimeLogPrompt(row.item)" href="#">
                                <i class="tim-icons text-danger icon-trash-simple mr-2"></i> Delete time log
                              </a>
                            </el-dropdown-menu>
                          </template>
                        </el-dropdown>
                        <span v-else class="table-muted">-</span>
                      </template>
                    </b-table>
                    </div>
                  </div>
                </div>

                <div class="d-block d-sm-none" v-if="time_logs.length > 0">
                  <div class="row">
                    <div class="col">
                      <b-table striped hover :items="time_logs" :fields="['time_log']">
                        <template v-slot:cell(time_log)="row">
                          <div class="row">
                            <div class="col">
                              <h5 class="mb-2">
                                <span>
                                  {{ formatMinutesToTimeString(row.item.duration_minutes || 0) }}
                                </span>
                              </h5>
                            </div>
                            <div class="col text-right">
                              <badge v-if="row.item.category_name" :style="{'background-color': row.item.category_color}">
                                {{ row.item.category_name }}
                              </badge>
                            </div>
                          </div>
                          <div class="row">
                            <div class="col">
                              <p class="mb-1">
                                <strong>{{ row.item.project_name || 'No project' }}</strong>
                                <span v-if="row.item.ticket_key"> - {{ row.item.ticket_key }}</span>
                              </p>
                            </div>
                          </div>
                          <div class="row">
                            <div class="col">
                              <small>Started: {{ getLocalDateTime(row.item.start_time) }}</small>
                            </div>
                          </div>
                          <div class="row" v-if="row.item.end_time && !row.item.is_running">
                            <div class="col">
                              <small>Ended: {{ getLocalDateTime(row.item.end_time) }}</small>
                            </div>
                          </div>
                          <div class="row" v-if="row.item.notes">
                            <div class="col">
                              <small>{{ row.item.notes }}</small>
                            </div>
                          </div>
                          <div class="row">
                            <div class="col">
                              <small>User: {{ row.item.user_name }}</small>
                            </div>
                          </div>

                          <!-- Mobile Action Buttons -->
                          <div v-if="!row.item.is_running && (hasPermissions('time_log_update') || hasPermissions('time_log_delete'))"
                               class="btn-group btn-group-toggle mt-3 mb-2" data-toggle="buttons" style="width:100%">
                            <label class="btn btn-sm btn-primary btn-simple" v-if="hasPermissions('time_log_update')">
                              <base-button
                                @click="editTimeLogPrompt(row.item)"
                                type="primary"
                                class="btn btn-link"
                                size="sm"
                                icon
                              ><i class="tim-icons icon-pencil"></i>
                              </base-button>
                            </label>
                            <label class="btn btn-sm btn-primary btn-simple" v-if="hasPermissions('time_log_delete')">
                              <base-button
                                @click="archiveTimeLogPrompt(row.item)"
                                type="primary"
                                class="btn btn-link"
                                size="sm"
                                icon
                              ><i class="tim-icons icon-trash-simple"></i>
                              </base-button>
                            </label>
                          </div>
                        </template>
                      </b-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-show="hasPermissions('time_log_create')" style="padding: 8px 0;">
              <base-button size="sm" type="primary" @click="openAddTimeLogModal()">
                <i class="tim-icons icon-simple-add mr-1"></i> Add time log
              </base-button>
            </div>
          </div>

          <!--  ATTACHMENTS TAB  -->
          <div v-show="activeTabName === 'attachments'">
            <!--  Drop zone  -->
            <div class="attachment-dropzone"
                 :class="{ 'dropzone-active': isDragging }"
                 @dragenter.prevent="isDragging = true"
                 @dragover.prevent="isDragging = true"
                 @dragleave.prevent="isDragging = false"
                 @drop.prevent="handleDrop"
                 @click="$refs.attachmentInput.click()">
              <i class="tim-icons icon-cloud-upload-94"></i>
              <span>Drop files here or click to browse</span>
              <span class="dropzone-hint">Max 25 MB per file. Images, PDFs, documents, and spreadsheets accepted.</span>
            </div>
            <input ref="attachmentInput" type="file" multiple
                   accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip"
                   style="display: none;" @change="handleFileSelect" />

            <!--  Upload progress  -->
            <div v-for="(f, i) in uploadingFiles" :key="'uploading-' + i" class="attachment-uploading">
              <i class="el-icon-loading"></i>
              <span>
                {{ f.name }}<span v-if="f.progress != null"> — {{ f.progress }}%</span><span v-if="f.retrying"> (retrying)</span>
              </span>
            </div>

            <!--  Error messages  -->
            <div v-for="(err, i) in uploadErrors" :key="'err-' + i" class="attachment-error">
              <span>{{ err }}</span>
              <i class="tim-icons icon-simple-remove" @click="uploadErrors.splice(i, 1)" style="cursor: pointer;"></i>
            </div>

            <!--  Attachment cards  -->
            <div v-if="attachments.length > 0" class="attachment-cards">
              <div v-for="att in attachments" :key="'att-' + att.id"
                   class="attachment-card"
                   :class="{ 'attachment-deleting': deletingAttachmentId === att.id }">
                <!--  Preview area  -->
                <div class="attachment-preview" @click="previewAttachment(att)">
                  <template v-if="isImage(att.content_type)">
                    <i class="tim-icons icon-image-02" style="font-size: 24px; color: #e14eca;"></i>
                  </template>
                  <template v-else-if="att.content_type === 'application/pdf'">
                    <i class="tim-icons icon-paper" style="font-size: 24px; color: #ef5350;"></i>
                    <span class="file-ext">PDF</span>
                  </template>
                  <template v-else-if="isSpreadsheet(att.content_type)">
                    <i class="tim-icons icon-chart-bar-32" style="font-size: 24px; color: #66bb6a;"></i>
                    <span class="file-ext">{{ getExtension(att.file_name) }}</span>
                  </template>
                  <template v-else-if="isDocument(att.content_type)">
                    <i class="tim-icons icon-single-copy-04" style="font-size: 24px; color: #42a5f5;"></i>
                    <span class="file-ext">{{ getExtension(att.file_name) }}</span>
                  </template>
                  <template v-else>
                    <i class="tim-icons icon-attach-87 table-muted" style="font-size: 24px;"></i>
                    <span class="file-ext">{{ getExtension(att.file_name) }}</span>
                  </template>
                </div>
                <!--  File info  -->
                <div class="attachment-info">
                  <span class="attachment-name" :title="att.file_name">{{ att.file_name }}</span>
                  <span class="attachment-meta">
                    {{ formatFileSize(att.file_size) }}
                    <span class="comment-dot">&middot;</span>
                    {{ att.uploaded_by_first_name }} {{ att.uploaded_by_last_name }}
                  </span>
                </div>
                <!--  Actions  -->
                <div class="attachment-actions">
                  <i class="tim-icons icon-cloud-download-93" title="Download" @click="downloadAttachment(att)"></i>
                  <i class="tim-icons icon-trash-simple" title="Delete" @click="confirmDeleteAttachment(att)"></i>
                </div>
              </div>
            </div>
          </div>

          <!--  Image lightbox  -->
          <div v-if="lightboxImage" class="attachment-lightbox" @click.self="lightboxImage = null">
            <div class="lightbox-content">
              <img :src="lightboxImage" />
              <i class="tim-icons icon-simple-remove lightbox-close" @click="lightboxImage = null"></i>
            </div>
          </div>

        </ticket-card>
      </div>

      <!--  SIDEBAR  -->
      <div class="col-lg-4">
        <!--  STATUS + WATCHERS — above the card  -->
        <div class="sidebar-status-row">
          <el-select v-model="status" @change="autoSaveMetadata('status')" class="select-primary sidebar-status-select"
                     value-key="id" placeholder="Status" :popper-append-to-body="false">
            <el-option v-for="(s, index) in statuses" class="select-primary" :label="s.name"
                       :value="{id: s.id, name: s.name, lifecycle_status: s.lifecycle_status, label_color: s.label_color}"
                       :key="'S' + index">
              <span style="float: left"><badge :style="{'background-color' : s.label_color}">{{ s.name }}</badge></span>
            </el-option>
          </el-select>
          <div>
            <el-popover placement="bottom-end" width="350" trigger="click" popper-class="watcher-popover">
              <div class="watcher-popover-content">
                <div>
                  <a v-if="!ticket.watching" href="#" @click.prevent="modifyTicket()" class="dropdown-item mt-2 mb-2">
                    <i class="tim-icons el-icon-view text-default mr-2"></i> Start watching
                  </a>
                  <a v-else href="#" @click.prevent="modifyTicket()" class="dropdown-item mt-2 mb-2">
                    <i class="tim-icons el-icon-view text-primary mr-2"></i> Stop watching
                  </a>
                  <hr class="my-2" style="border-color: rgba(0,0,0,0.1);">
                </div>
                <div v-if="watchers.length > 0">
                  <div class="mb-2">
                    <small class="text-uppercase watcher-label">Watching this ticket</small>
                  </div>
                  <div v-for="(watcher, index) in watchers" :key="'W' + index" class="watcher-item">
                    <div class="d-flex align-items-center">
                      <img v-if="watcher.img" :src="watcher.img" class="avatar mr-2" alt="avatar"
                           style="width: 28px; height: 28px; object-fit: cover; border-radius: 50%;" />
                      <div v-else class="avatar mr-2 d-flex align-items-center justify-content-center"
                           style="width: 28px; height: 28px; background-color: #1d8cf8; border-radius: 50%; font-weight: bold; color: white; font-size: 11px;">
                        {{getInitials(watcher.first_name, watcher.last_name)}}
                      </div>
                      <span class="watcher-name">{{watcher.first_name}} {{watcher.last_name}}</span>
                    </div>
                    <a href="#" @click.prevent="removeWatcher(watcher)" class="text-default" style="text-decoration: none; padding: 4px;">
                      <i class="tim-icons icon-simple-remove" style="font-size: 12px;"></i>
                    </a>
                  </div>
                </div>
                <div v-else class="no-watchers-message">
                  <p class="mb-0">No current watchers</p>
                </div>
                <div>
                  <hr class="my-2" style="border-color: rgba(0,0,0,0.1);">
                  <div v-if="!showAddWatchers">
                    <a href="#" @click.prevent="showAddWatchers = true" class="dropdown-item mt-2 mb-2">
                      <i class="tim-icons icon-simple-add text-default mr-2"></i> Add watchers
                    </a>
                  </div>
                  <div v-else>
                    <base-input style="margin-bottom: 10px;">
                      <el-select v-model="selectedWatchersToAdd" multiple filterable placeholder="Search users..."
                                 class="select-primary" style="width: 100%;" size="small">
                        <el-option class="select-primary" v-for="user in availableUsersForWatching" :key="user.id"
                                   :label="user.name" :value="user.id">
                          <div class="d-flex align-items-center">
                            <img v-if="user.img" :src="user.img" class="avatar mr-2" alt="avatar"
                                 style="width: 20px; height: 20px; object-fit: cover; border-radius: 50%;" />
                            <div v-else class="mr-2 d-flex align-items-center justify-content-center"
                                 style="width: 20px; height: 20px; background-color: #1d8cf8; border-radius: 50%; font-size: 9px; font-weight: bold; color: white;">
                              {{getInitials(user.first_name, user.last_name)}}
                            </div>
                            <span style="font-size: 13px;">{{user.name}}</span>
                          </div>
                        </el-option>
                      </el-select>
                    </base-input>
                    <div class="d-flex" style="gap: 8px;">
                      <base-button size="sm" type="secondary" @click="showAddWatchers = false; selectedWatchersToAdd = [];" style="flex: 1;">Cancel</base-button>
                      <base-button size="sm" type="primary" @click="addWatchersInline()" style="flex: 1;">Add</base-button>
                    </div>
                  </div>
                </div>
              </div>
              <base-button slot="reference" size="sm" :type="ticket.watching ? 'primary' : 'default'">
                <i class="tim-icons el-icon-view mr-1"></i>
                <span class="ml-1">{{ticket.watcher_count || 0}}</span>
              </base-button>
            </el-popover>
          </div>
          <el-dropdown trigger="click" style="margin-left: auto;">
            <base-button size="sm" type="default" style="height: 28px; padding: 0 10px; line-height: 26px; margin-bottom: 0;">
              <i class="tim-icons el-icon-more"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a class="dropdown-item" href="#" @click.prevent="archiveTicketPrompt">
                  <i class="tim-icons text-danger icon-trash-simple mr-2"></i> Archive ticket
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <transition name="fade-saved"><span v-if="savedField === 'status'" class="field-saved" style="position: static; transform: none;"><i class="tim-icons icon-check-2"></i> Saved</span></transition>
        </div>

        <ticket-card header="Details" compact>
          <ticket-detail-field label="Assignee" interactive :saved="savedField === 'assignee'">
            <template v-if="assigneeUser">
              <img v-if="assigneeUser.img" :src="assigneeUser.img" class="prop-avatar-img" />
              <div v-else class="prop-avatar-initials">{{ getInitials(assigneeUser.first_name, assigneeUser.last_name) }}</div>
            </template>
            <el-select v-model="assignee" @change="autoSaveMetadata('assignee')" class="select-primary detail-select"
                       value-key="id" placeholder="Unassigned" autocomplete="off">
              <el-option v-for="(u, index) in users" class="select-primary" :label="u.name"
                         :value="{id: u.id, name: u.name}" :key="'U' + index"></el-option>
            </el-select>
          </ticket-detail-field>

          <ticket-detail-field v-if="reporterUser" label="Reporter">
            <img v-if="reporterUser.img" :src="reporterUser.img" class="prop-avatar-img" />
            <div v-else class="prop-avatar-initials">{{ getInitials(reporterUser.first_name, reporterUser.last_name) }}</div>
            <span class="detail-text-value">{{ reporterUser.name }}</span>
          </ticket-detail-field>

          <ticket-detail-field label="Priority" interactive :saved="savedField === 'priority'">
            <i v-if="priority && priority.icon" :class="priority.icon" :style="{ color: priority.color, fontSize: '14px' }"></i>
            <el-select v-model="priority" @change="autoSaveMetadata('priority')" class="select-primary detail-select"
                       value-key="id" placeholder="Priority">
              <el-option v-for="(pt, index) in priorities" class="select-primary" :label="pt.name"
                         :value="{id: pt.id, name: pt.name, icon: pt.icon, color: pt.color}" :key="'PT' + index">
                <span style="float: left"><i :class="pt.icon" class="mr-3" :style="{'color': pt.color}"></i>{{ pt.name }}</span>
              </el-option>
            </el-select>
          </ticket-detail-field>

          <ticket-detail-field label="Ticket type" interactive :saved="savedField === 'ticket_type'">
            <badge v-if="ticket_type && ticket_type.name"
                   :style="{'background-color': ticket_type.label_color || '#1d8cf8'}"
                   style="font-size: 11px;">{{ ticket_type.name }}</badge>
            <el-select v-model="ticket_type" @change="autoSaveMetadata('ticket_type')" class="select-primary detail-select detail-select--hidden"
                       value-key="id" placeholder="Ticket type">
              <el-option v-for="(t, i) in ticket_types.filter(t => t.archived === 0)" class="select-primary"
                         :label="t.name" :value="{id: t.id, name: t.name, label_color: t.label_color}" :key="'T' + i"></el-option>
            </el-select>
          </ticket-detail-field>

          <ticket-detail-field label="Due date" interactive :saved="savedField === 'due_date'">
            <i class="el-icon-date detail-icon"></i>
            <base-input class="detail-datepicker mb-0">
              <el-date-picker v-model="due_date" @change="autoSaveMetadata('due_date')" type="date"
                placeholder="No due date" :format="day_of_week + dateFormatCal"
                value-format="yyyy-MM-dd" clearable style="width: 100%">
              </el-date-picker>
            </base-input>
          </ticket-detail-field>

          <ticket-detail-field label="Time estimate" interactive @click="editTimeEstimate">
            <i class="tim-icons icon-watch-time detail-icon"></i>
            <span class="detail-text-value">{{ formatMinutesToTimeString(ticket.time_estimate_minutes) }}</span>
          </ticket-detail-field>
        </ticket-card>
      </div>
    </div>

    <!--  TIME ESTIMATE MODAL  -->
    <modal :show.sync="modals.time_tracking"
           body-classes="p-0"
           modal-classes="modal-dialog-centered ">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Edit time estimate</h4>
        <div class="row" >
          <div class="col-lg-6">
            <base-input
                        v-model="time_estimate" :error="errs.time_estimate"
                        required label="Time estimate"></base-input>
          </div>
        </div>
        <div class="row" >
          <div class="col">
            <p class="text-white">Use the format: 2w 4d 6h 45m</p>
            <ul class="text-white small">
              <li>w = weeks</li>
              <li>d = days</li>
              <li>h = hours</li>
              <li>m = minutes</li>
            </ul>
          </div>
        </div>
        <div class="row">
          <div class="col text-center">
            <base-button type="secondary" @click="modals.time_tracking = false">Cancel</base-button>
            <base-button  class="ml-3" type="primary" @click="updateTimeEstimate()">Save</base-button>
          </div>
        </div>
      </card>
    </modal>

    <!--  ADD/EDIT TIME LOG MODAL  -->
    <modal :show.sync="modals.time_log"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <div class="row">
          <div class="col">
            <h4 class="card-title">{{ editingTimeLog ? 'Update time log' : 'Add time log' }}</h4>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <base-input label="Category" placeholder="Select a category" :error="errs.category_id" required>
              <el-select v-model="timeLogForm.category_id"
                         placeholder="Select category"
                         class="select-primary"
                         filterable
                         clearable
                         style="width: 100%"
                         :class="{ 'is-invalid': errs.category_id }">
                <el-option
                  v-for="category in categories_list"
                  :key="category.id"
                  :label="category.name"
                  :value="category.id">
                  <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 8px;"
                    :style="{'background-color': category.label_color || category.color}"></span>
                    {{ category.name }}
                  </div>
                </el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <!-- Project and Ticket display (read-only) -->
        <div class="row">
          <div class="col-lg-6">
            <base-input label="Project" readonly>
              <input type="text" class="form-control" :value="ticket.project_name" readonly>
            </base-input>
          </div>
          <div class="col-lg-6">
            <base-input label="Ticket" readonly>
              <input type="text" class="form-control" :value="`${ticket.ticket_key} ${ticket.title}`" readonly>
            </base-input>
          </div>
        </div>

        <!-- Start Time Section -->
        <div class="row">
          <div class="col">
            <h6 class="mb-3">Start time</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6">
            <base-input label="Start date" placeholder="Select start date" :error="errs.start_date" required>
              <el-date-picker
                v-model="timeLogForm.start_date"
                type="date"
                placeholder="Select date"
                :format="day_of_week + '' + dateFormatCal"
                value-format="yyyy-MM-dd"
                style="width: 100%">
              </el-date-picker>
            </base-input>
          </div>
          <div class="col-lg-6">
            <base-input label="Start time" placeholder="Select start time" :error="errs.start_time" required>
              <el-time-picker
                v-model="timeLogForm.start_time_only"
                placeholder="Select time"
                :format="picker_time_format"
                value-format="HH:mm:ss"
                style="width: 100%">
              </el-time-picker>
            </base-input>
          </div>
        </div>

        <!-- End Time Section -->
        <div class="row">
          <div class="col">
            <h6 class="mb-3">End time</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6">
            <base-input label="End date" placeholder="Select end date" :error="errs.end_date" required>
              <el-date-picker
                v-model="timeLogForm.end_date"
                type="date"
                placeholder="Select date"
                :format="day_of_week + '' + dateFormatCal"
                value-format="yyyy-MM-dd"
                style="width: 100%">
              </el-date-picker>
            </base-input>
          </div>
          <div class="col-lg-6">
            <base-input label="End time" placeholder="Select end time" :error="errs.end_time" required>
              <el-time-picker
                v-model="timeLogForm.end_time_only"
                placeholder="Select time"
                :format="picker_time_format"
                value-format="HH:mm:ss"
                style="width: 100%">
              </el-time-picker>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Notes" placeholder="Add notes (optional)">
        <textarea class="form-control"
                  v-model="timeLogForm.notes"
                  rows="3"
                  placeholder="Enter any notes about this time entry..."></textarea>
            </base-input>
          </div>
        </div>

        <div class="row text-center mt-3">
          <div class="col">
            <base-button type="secondary" @click="closeTimeLogModal()" :disabled="savingTimeLog">Cancel</base-button>
            <base-button class="ml-2" type="primary" @click="saveTimeLog()" :disabled="savingTimeLog">
              <i v-if="savingTimeLog" class="el-icon-loading mr-1"></i>{{ savingTimeLog ? 'Saving...' : (editingTimeLog ? 'Update' : 'Save') }}
            </base-button>
          </div>
        </div>

      </card>
    </modal>
  </div>
</template>

<script>
import {Dropdown, DropdownItem, DropdownMenu, Popover, Progress, TimePicker} from "element-ui";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import {Badge, BaseSwitch} from "@/components";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import TabRail from '@/components/TabRail';
import {Auth} from "aws-amplify";
import {localDateTime, formatGroupDate, formatGroupTime} from "@/plugins/dates";
import _ from "lodash";
import {BTable} from "bootstrap-vue";
import moment from "moment-timezone";
import TicketCard from "@/components/Tickets/TicketCard";
import TicketDetailField from "@/components/Tickets/TicketDetailField";
import TicketDescription from "@/components/Tickets/TicketDescription";
import TicketComments from "@/components/Tickets/TicketComments";
import draggable from 'vuedraggable';

export default {
  name: "ProjectTicket",
  components: {
    TabRail,
    BTable,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Popover.name]: Popover,
    [Progress.name]: Progress,
    [TimePicker.name]: TimePicker,
    BreadcrumbItem,
    Breadcrumb,
    BaseSwitch,
    Badge,
    TicketCard,
    TicketDetailField,
    TicketDescription,
    TicketComments,
    draggable
  },
  computed: {
    categories() {
      const cats = [
        { key: 'comments', name: 'Comments', count: this.comments.length, icon: 'tim-icons icon-chat-33', active: this.activeTabName === 'comments' }
      ];

      // Subtasks tab — only show on parent tickets (not on subtasks)
      if (!this.parentTicket) {
        cats.push({ key: 'subtasks', name: 'Subtasks', count: this.subtasks.length, icon: 'tim-icons icon-bullet-list-67', active: this.activeTabName === 'subtasks' });
      }

      cats.push({ key: 'attachments', name: 'Attachments', count: this.attachments.length, icon: 'tim-icons icon-attach-87', active: this.activeTabName === 'attachments' });

      if (this.hasPermissions('time_log_create') || this.hasPermissions('time_log_view')) {
        cats.push({ key: 'time_tracking', name: 'Time tracking', count: this.time_logs.length, icon: 'tim-icons icon-watch-time', active: this.activeTabName === 'time_tracking' });
      }

      return cats;
    },
    tabRailTabs() {
      return this.categories.map(c => ({
        name: c.count != null && c.count > 0 ? `${c.name} (${c.count})` : c.name,
        shortName: c.name
      }));
    },
    activeTabIndex() {
      const i = this.categories.findIndex(c => c.key === this.activeTabName);
      return i < 0 ? 0 : i;
    },
    totalTimeSpent() {
      return this.time_logs.reduce((total, log) => total + (log.duration_minutes || 0), 0);
    },
    availableUsersForWatching() {
      const watcherIds = this.watchers.map(w => w.user_id);
      return this.users.filter(user => !watcherIds.includes(user.id));
    },
    completedSubtaskCount() {
      return this.subtasks.filter(s => this.isSubtaskCompleted(s)).length;
    },
    subtaskCompletionPercent() {
      if (this.subtasks.length === 0) return 0;
      return Math.round((this.completedSubtaskCount / this.subtasks.length) * 100);
    },
    assigneeUser() {
      if (!this.assignee || !this.assignee.id) return null;
      return this.users.find(u => u.id === this.assignee.id) || null;
    },
    isDarkMode() {
      return !document.body.classList.contains('white-content');
    },
    reporterUser() {
      if (!this.ticket || !this.ticket.reporter_id) return null;
      return this.users.find(u => u.id === this.ticket.reporter_id) || null;
    },
  },
  watch: {
    '$route.params.ticket_id'(newId) {
      if (newId) {
        this.ticketId = newId;
        this.loading = true;
        this.activeTabName = 'comments';
        this.subtasks = [];
        this.parentTicket = null;
        this.showCreateSubtaskForm = false;
        this.editingSubtaskId = null;
        this.getTicket();
      }
    },
    status() {
      this.applyStatusColor();
    }
  },
  data() {
    return {
      loading: false,
      projectId: '',
      alertDialog: { show: false, title: '', message: '', variant: 'info' },
      confirmDialog: { show: false, title: '', message: '', confirmText: 'Confirm', resolve: null },
      ticketId: '',
      ticket: {},
      j: 0,
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      picker_time_format: 'hh:mm A',
      day_of_week: '',
      dateFormatCal: '',
      users: [],
      errs: {
        ticket_type: '',
        feature: '',
        summary: '',
        time_estimate: '',
        category_id: '',
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: ''
      },
      ticket_types: [],
      statuses: [],
      priorities: [
        {id: 1, name: "Highest", icon: 'fa fa-angle-double-up', color: '#ec250d'},
        {id: 2, name: "High", icon: 'fa fa-angle-up', color: '#ec250d'},
        {id: 3, name: "Medium", icon: 'tim-icons icon-simple-delete', color: '#ff8d72'},
        {id: 4, name: "Low", icon: 'fa fa-angle-down', color: '#1d8cf8'},
        {id: 5, name: "Lowest", icon: 'fa fa-angle-double-down', color: '#1d8cf8'},
      ],
      comments: [],
      time_logs: [],
      categories_list: [],
      watchers: [],
      selectedWatchersToAdd: [],
      showAddWatchers: false,
      title: '',
      editingTitle: false,
      originalTitle: '',
      description: '',
      status: {id: 1},
      priority: {id: 3},
      ticket_type: {id: 1},
      assignee: {},
      modals: {
        time_tracking: false,
        time_log: false
      },
      activeTabName: 'comments',
      subtasks: [],
      parentTicket: null,
      showCreateSubtaskForm: false,
      newSubtask: { title: '', priority: 3, status: 1, assignee: null, due_date: null },
      editingSubtaskId: null,
      editSubtask: { title: '', priority: null, status: null, assignee: null, due_date: null },
      reorderAnnouncement: '',
      due_date: null,
      time_estimate: '0m',
      spentPercent: 0,
      overEstimate: false,
      editingTimeLog: false,
      timeLogForm: {
        category_id: null,
        start_date: null,
        start_time_only: null,
        end_date: null,
        end_time_only: null,
        notes: ''
      },
      savedDescriptionContent: '',
      deletingCommentId: null,
      saveFeedback: '',
      saveFeedbackTimeout: null,
      savedField: null,
      savedFieldTimeout: null,
      savingComment: false,
      savingDescription: false,
      savingTimeLog: false,
      attachments: [],
      attachmentsLoaded: false,
      uploadingFiles: [],
      deletingAttachmentId: null,
      lightboxImage: null,
      isDragging: false,
      uploadErrors: []
    }
  },
  methods: {

    // --- Auto-save methods ---

    async archiveTicketPrompt() {
      const ok = await this.askConfirm('Archive this ticket?', 'This ticket will be archived.', 'Archive')
      if (ok) this.archiveTicket()
    },
    showAlert(title, message, variant) {
      this.alertDialog = { show: true, title, message, variant }
    },
    askConfirm(title, message, confirmText) {
      return new Promise(resolve => {
        this.confirmDialog = { show: true, title, message, confirmText, resolve }
      })
    },
    resolveConfirm(value) {
      const resolve = this.confirmDialog.resolve
      this.confirmDialog = { show: false, title: '', message: '', confirmText: 'Confirm', resolve: null }
      if (resolve) resolve(value)
    },

    async archiveTicket() {
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}`
        await axios.delete(url, config)
        this.$notify({ type: 'success', message: 'Ticket archived', timeout: 2000 })
        this.$router.push('/projects/' + this.projectId)
      } catch (err) {
        console.log(err)
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Failed to archive ticket' })
        }
      }
    },
    applyStatusColor() {
      this.$nextTick(() => {
        const inner = this.$el.querySelector('.sidebar-status-select .el-input__inner');
        if (inner && this.status && this.status.label_color) {
          inner.style.setProperty('background-color', this.status.label_color, 'important');
          // Measure text width and size every element in the chain
          const span = document.createElement('span');
          span.style.cssText = 'position:absolute;visibility:hidden;font:600 12px -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;white-space:nowrap;';
          span.textContent = this.status.name || '';
          document.body.appendChild(span);
          const w = span.offsetWidth + 38;
          document.body.removeChild(span);
          const px = w + 'px';
          inner.style.setProperty('width', px, 'important');
          const input = this.$el.querySelector('.sidebar-status-select .el-input');
          if (input) input.style.setProperty('width', px, 'important');
          const select = this.$el.querySelector('.sidebar-status-select');
          if (select) select.style.setProperty('width', px, 'important');
        }
      });
    },

    showFieldSaved(fieldName) {
      this.savedField = fieldName;
      clearTimeout(this.savedFieldTimeout);
      this.savedFieldTimeout = setTimeout(() => { this.savedField = null; }, 1500);
    },

    async autoSaveMetadata(fieldName) {
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' +
          this.companyGroupId + '/projects/' + this.projectId + '/tickets/' + this.ticketId

        const data = {
          title: this.title,
          description: this.savedDescriptionContent || '',
          priority: this.priority.id,
          status: this.status.id,
          assignee: this.assignee.id,
          type_id: this.ticket_type ? this.ticket_type.id : null,
          due_date: this.due_date
        }

        await axios.put(url, data, config)
        if (fieldName) {
          this.showFieldSaved(fieldName)
        } else {
          this.$notify({ type: 'success', message: 'Changes saved', timeout: 2000 })
        }
      } catch (err) {
        console.log(err)
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Failed to save changes' })
        }
      }
    },

    async saveDescription(newDescription) {
      this.savingDescription = true
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' +
          this.companyGroupId + '/projects/' + this.projectId + '/tickets/' + this.ticketId

        const data = {
          title: this.title,
          description: newDescription,
          priority: this.priority.id,
          status: this.status.id,
          assignee: this.assignee.id,
          type_id: this.ticket_type ? this.ticket_type.id : null,
          due_date: this.due_date
        }

        await axios.put(url, data, config)
        this.savedDescriptionContent = newDescription
        this.savingDescription = false
        if (this.$refs.description) this.$refs.description.close()
        this.$notify({ type: 'success', message: 'Description saved', timeout: 2000 })
      } catch (err) {
        this.savingDescription = false
        console.log(err)
        if (err.response?.status === 401) {
          this.$router.push('/forbidden')
        } else {
          this.$notify({ type: 'danger', message: 'Failed to save description' })
        }
      }
    },

    cancelEditTitle() {
      this.title = this.originalTitle
      this.editingTitle = false
    },

    blurTitle() {
      if (!this.editingTitle) return
      this.editingTitle = false
      if (this.title.trim() && this.title !== this.originalTitle) {
        this.autoSaveMetadata()
      } else if (!this.title.trim()) {
        this.title = this.originalTitle
      }
    },

    // --- Existing methods (preserved) ---

    editTimeLogPrompt(timeLog) {
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const toWallClock = (utc) => tz ? moment.utc(utc).tz(tz) : moment(utc)

      const startMoment = toWallClock(timeLog.start_time);
      const startDate = startMoment.format('YYYY-MM-DD');
      const startTime = startMoment.format('HH:mm:ss');

      let endDate = null;
      let endTime = null;
      if (timeLog.end_time) {
        const endMoment = toWallClock(timeLog.end_time);
        endDate = endMoment.format('YYYY-MM-DD');
        endTime = endMoment.format('HH:mm:ss');
      }

      // Set up the edit form with current values
      this.timeLogForm = {
        id: timeLog.id,
        category_id: timeLog.time_log_category_id,
        start_date: startDate,
        start_time_only: startTime,
        end_date: endDate,
        end_time_only: endTime,
        notes: timeLog.notes || ''
      };

      this.editingTimeLog = true;
      this.modals.time_log = true;
    },
    editTimeEstimate(){
      this.modals.time_tracking = true
      this.time_estimate = this.formatMinutesToTimeString(this.ticket.time_estimate_minutes)
    },
    openAddTimeLogModal() {
      this.editingTimeLog = false;
      this.resetTimeLogForm();
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const now = tz ? moment.tz(tz) : moment()
      const today = now.format('YYYY-MM-DD')
      const nowTime = now.format('HH:mm:ss')
      this.timeLogForm.start_date = today
      this.timeLogForm.start_time_only = nowTime
      this.timeLogForm.end_date = today
      this.timeLogForm.end_time_only = nowTime
      this.modals.time_log = true;
    },
    editTimeLog(log) {
      this.editingTimeLog = true;
      this.timeLogForm = {
        id: log.id,
        category_id: log.time_log_category_id,
        start_date: log.start_time.split('T')[0],
        start_time_only: log.start_time.split('T')[1].substring(0, 8),
        end_date: log.end_time.split('T')[0],
        end_time_only: log.end_time.split('T')[1].substring(0, 8),
        notes: log.notes || ''
      };
      this.modals.time_log = true;
    },
    resetTimeLogForm() {
      this.timeLogForm = {
        category_id: null,
        start_date: null,
        start_time_only: null,
        end_date: null,
        end_time_only: null,
        notes: ''
      };
    },
    closeTimeLogModal() {
      this.modals.time_log = false;
      this.editingTimeLog = false;
      this.resetTimeLogForm();
      this.errs.category_id = '';
      this.errs.start_date = '';
      this.errs.start_time = '';
      this.errs.end_date = '';
      this.errs.end_time = '';
    },
    validateTimeLog() {
      let errors = 0;

      if (!this.timeLogForm.category_id) {
        this.errs.category_id = 'Category is required';
        errors++;
      } else {
        this.errs.category_id = '';
      }

      if (!this.timeLogForm.start_date) {
        this.errs.start_date = 'Start date is required';
        errors++;
      } else {
        this.errs.start_date = '';
      }

      if (!this.timeLogForm.start_time_only) {
        this.errs.start_time = 'Start time is required';
        errors++;
      } else {
        this.errs.start_time = '';
      }

      if (!this.timeLogForm.end_date) {
        this.errs.end_date = 'End date is required';
        errors++;
      } else {
        this.errs.end_date = '';
      }

      if (!this.timeLogForm.end_time_only) {
        this.errs.end_time = 'End time is required';
        errors++;
      } else {
        this.errs.end_time = '';
      }

      // Validate start is before end
      if (errors === 0) {
        const startDate = typeof this.timeLogForm.start_date === 'string'
          ? this.timeLogForm.start_date.substring(0, 10)
          : moment(this.timeLogForm.start_date).format('YYYY-MM-DD');
        const endDate = typeof this.timeLogForm.end_date === 'string'
          ? this.timeLogForm.end_date.substring(0, 10)
          : moment(this.timeLogForm.end_date).format('YYYY-MM-DD');
        const startTime = typeof this.timeLogForm.start_time_only === 'string'
          ? this.timeLogForm.start_time_only
          : moment(this.timeLogForm.start_time_only).format('HH:mm:ss');
        const endTime = typeof this.timeLogForm.end_time_only === 'string'
          ? this.timeLogForm.end_time_only
          : moment(this.timeLogForm.end_time_only).format('HH:mm:ss');

        const start = moment(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm:ss');
        const end = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm:ss');

        if (!start.isValid() || !end.isValid()) {
          this.errs.end_date = 'Invalid date or time';
          errors++;
        } else if (end.isSameOrBefore(start)) {
          this.errs.end_date = 'End must be after start';
          errors++;
        }
      }

      return errors === 0;
    },
    async saveTimeLog() {
      if (!this.validateTimeLog()) {
        return;
      }

      this.savingTimeLog = true;
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };

      const sd = typeof this.timeLogForm.start_date === 'string' ? this.timeLogForm.start_date.substring(0, 10) : moment(this.timeLogForm.start_date).format('YYYY-MM-DD');
      const st = typeof this.timeLogForm.start_time_only === 'string' ? this.timeLogForm.start_time_only : moment(this.timeLogForm.start_time_only).format('HH:mm:ss');
      const ed = typeof this.timeLogForm.end_date === 'string' ? this.timeLogForm.end_date.substring(0, 10) : moment(this.timeLogForm.end_date).format('YYYY-MM-DD');
      const et = typeof this.timeLogForm.end_time_only === 'string' ? this.timeLogForm.end_time_only : moment(this.timeLogForm.end_time_only).format('HH:mm:ss');

      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const toUtcIso = (date, t) => {
        const local = `${date} ${t}`
        return (tz ? moment.tz(local, tz) : moment(local)).utc().toISOString()
      }

      const data = {
        ticket_id: this.ticketId,
        time_log_category_id: this.timeLogForm.category_id,
        start_time: toUtcIso(sd, st),
        end_time: toUtcIso(ed, et),
        notes: this.timeLogForm.notes
      };

      try {
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}/time-logs`;

        if (this.editingTimeLog) {
          data.project_id = this.projectId
          data.category_id = this.timeLogForm.category_id

          url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${this.timeLogForm.id}`;
          await axios.put(url, data, config);
          this.$notify({ type: 'success', message: 'Time log updated', timeout: 2000 });
        } else {
          await axios.post(url, data, config);
          this.$notify({ type: 'success', message: 'Time log added', timeout: 2000 });
        }

        this.closeTimeLogModal();
        this.getTicket(true);
      } catch (err) {
        console.log(err);
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else if (err.response?.status === 400) {
          this.$notify({ type: 'danger', message: this.editingTimeLog ? err.response.data : err.response.data.details[0] });
        } else if (err.response?.status === 403) {
          // Location check (OTC-2926): past entries in a Block category are limited to admins and managers.
          let body = err.response.data
          if (typeof body === 'string') {
            try { body = JSON.parse(body) } catch (e) { body = null }
          }
          this.$notify({ type: 'danger', message: (body && body.error) || 'You do not have access to change this time log.' });
        }
      } finally {
        this.savingTimeLog = false;
      }
    },
    async archiveTimeLogPrompt(timeLog) {
      const ok = await this.askConfirm('Delete this time log?', 'The time log will be permanently deleted.', 'Delete');
      if (ok) this.deleteTimeLog(timeLog);
    },
    async deleteTimeLog(log) {
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/time-logs/${log.id}`;

      try {
        await axios.delete(url, config);
        this.$notify({ type: 'success', message: 'Time log deleted', timeout: 2000 });
        this.getTicket(true);
      } catch (err) {
        console.log(err);
        if (err.response?.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.$notify({ type: 'danger', message: 'Failed to delete time log' });
        }
      }
    },
    parseTimeStringToMinutes(timeString) {
      const regex = /(\d+)\s*(w|d|h|m)/g;
      let totalMinutes = 0;
      let match;

      const multipliers = {
        w: 7 * 24 * 60,
        d: 24 * 60,
        h: 60,
        m: 1
      };

      while ((match = regex.exec(timeString)) !== null) {
        const value = parseInt(match[1], 10);
        const unit = match[2];
        totalMinutes += value * (multipliers[unit] || 0);
      }

      return totalMinutes;
    },
    formatMinutesToTimeString(totalMinutes){
      if (!totalMinutes || totalMinutes === 0) return '0m';

      const MINUTES_IN_WEEK = 7 * 24 * 60;
      const MINUTES_IN_DAY = 24 * 60;
      const MINUTES_IN_HOUR = 60;

      const weeks = Math.floor(totalMinutes / MINUTES_IN_WEEK);
      totalMinutes %= MINUTES_IN_WEEK;

      const days = Math.floor(totalMinutes / MINUTES_IN_DAY);
      totalMinutes %= MINUTES_IN_DAY;

      const hours = Math.floor(totalMinutes / MINUTES_IN_HOUR);
      const minutes = totalMinutes % MINUTES_IN_HOUR;

      const parts = [];
      if (weeks > 0) parts.push(`${weeks}w`);
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

      return parts.join(' ');
    },
    getPercentCompleted(){
      let spent = this.totalTimeSpent;
      let estimate = (this.ticket.time_estimate_minutes !== null && this.ticket.time_estimate_minutes !== undefined)
        ? this.ticket.time_estimate_minutes : 0;

      if(spent > estimate){
        this.overEstimate = true
        let temp = spent
        spent = estimate
        estimate = temp
      }

      if (estimate === 0) {
        if (spent === 0) {
          this.spentPercent = 0;
        } else {
          this.spentPercent = 100;
        }
        return;
      }

      const percent = (spent / estimate) * 100;
      this.spentPercent = Math.min(100, Math.round(percent));
    },
    switchTab(index){
      if (index >= this.categories.length) {
        return;
      }

      this.activeTabName = this.categories[index].key
      this.j++

      if (this.activeTabName === 'attachments') {
        this.loadAttachments()
      }
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    async handleCommentCheckbox({ comment, html, revert }) {
      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
          '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/comments/' + comment.id;

        await axios.put(url, { comment: html }, config);
        this.$set(comment, 'comment', html);
      } catch (err) {
        console.log(err);
        revert();
        this.$notify({ type: 'danger', message: 'Failed to update checklist' });
      }
    },
    isAuthorized(user_id){
      let loggedInUser = Number(JSON.parse(localStorage.getItem('profile')).id)
      return user_id === loggedInUser
    },
    validateTicket(){
      let errors = 0
      if(this.title === ''){
        this.errs.summary = 'Ticket summary cannot be empty'
        errors++
      } else {
        this.errs.summary = ''
      }

      return !(errors > 0)
    },
    async loadAllComments() {
      const commentsToLoad = this.comments.filter(c => {
        if (c.comment && c.comment.length > 0) {
          return false;
        }
        if (c.has_content) {
          return true;
        }
        return false;
      });

      if (commentsToLoad.length === 0) {
        return;
      }

      const loadPromises = commentsToLoad.map(comment => this.loadCommentContent(comment));
      await Promise.all(loadPromises);
    },

    async loadCommentContent(comment) {
      if (comment.comment && comment.comment.length > 0) {
        return;
      }

      if (!comment.has_content) {
        return;
      }

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
          '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/comments/' + comment.id;
        const response = await axios.get(url, config);

        this.$set(comment, 'comment', response.data.content);

      } catch (error) {
        console.error(`Error loading comment ${comment.id}:`, error);
        this.$set(comment, 'comment', '<p style="color: red;">Error loading comment. Please refresh the page.</p>');
      }
    },
    async deleteComment(item){
      this.deletingCommentId = item.id
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/comments/' + item.id

      axios.delete(url, config)
        .then(response => {
          this.deletingCommentId = null
          const idx = this.comments.findIndex(c => c.id === item.id)
          if (idx > -1) this.comments.splice(idx, 1)
        })
        .catch(err => {
          this.deletingCommentId = null
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          }
        })
    },
    async updateComment({ comment, html }){
      this.savingComment = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/comments/' + comment.id

      axios.put(url, { comment: html }, config)
        .then(response => {
          this.savingComment = false
          if (this.$refs.ticketComments) this.$refs.ticketComments.reset()
          this.getTicket(true)
        })
        .catch(err => {
          this.savingComment = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          }
        })
    },
    async createComment(html){
      this.savingComment = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/comments'

      axios.post(url, { comment: html }, config)
        .then(response => {
          this.savingComment = false
          if (this.$refs.ticketComments) this.$refs.ticketComments.reset()
          this.getTicket(true)
        })
        .catch(err => {
          this.savingComment = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          }
        })
    },
    goToProject(){
      this.$router.push('/projects/' + this.projectId)
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return first + last;
    },
    async removeWatcher(watcher) {
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/watch/' + watcher.user_id;

      axios.delete(url, config)
        .then(response => {
          this.showFieldSaved('status');
          this.getTicket(true);
        })
        .catch(err => {
          console.log(err);
          if(err.response?.status == 401){
            this.$router.push('/forbidden');
          } else {
            this.$notify({ type: 'danger', message: 'Failed to remove watcher' });
          }
        });
    },
    async addWatchersInline() {
      if (this.selectedWatchersToAdd.length === 0) {
        this.$notify({ type: 'danger', message: 'Please select at least one user' });
        return;
      }

      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/watch';

      let data = {
        user_ids: this.selectedWatchersToAdd
      };

      axios.post(url, data, config)
        .then(response => {
          this.selectedWatchersToAdd = [];
          this.showAddWatchers = false;
          this.showFieldSaved('status');
          this.getTicket(true);
        })
        .catch(err => {
          console.log(err);
          if(err.response?.status == 401){
            this.$router.push('/forbidden');
          } else {
            this.$notify({ type: 'danger', message: 'Failed to add watchers' });
          }
        });
    },
    async modifyTicket(){
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + this.ticketId + '/watch'

      axios.post(url, {}, config)
        .then(response => {
          this.showFieldSaved('status')
          this.getTicket(true)
        })
        .catch(err => {
          console.log(err)
          if(err.response?.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.$notify({ type: 'danger', message: 'Failed to update watch status' })
          }
        })
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    isValidTimeString(timeString) {
      if(timeString === ''){
        return false
      }
      const validOrder = ['w', 'd', 'h', 'm'];
      const regex = /(\d+)\s*(w|d|h|m)/g;

      let seenUnits = new Set();
      let lastIndex = -1;
      let match;
      let inputTrimmed = timeString.trim();

      let reconstructed = '';

      while ((match = regex.exec(inputTrimmed)) !== null) {
        const [full, number, unit] = match;

        if (seenUnits.has(unit)) return false;
        seenUnits.add(unit);

        const indexInOrder = validOrder.indexOf(unit);
        if (indexInOrder <= lastIndex) return false;
        lastIndex = indexInOrder;

        reconstructed += (reconstructed ? ' ' : '') + `${number}${unit}`;
      }

      return reconstructed === inputTrimmed;
    },
    async updateTimeEstimate(){
      if(!this.isValidTimeString(this.time_estimate)){
        this.errs.time_estimate = 'Invalid time format'
        return
      } else {
        this.errs.time_estimate = ''
      }
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' +
        this.companyGroupId + '/projects/' + this.projectId + '/tickets/' + this.ticketId
      const data = {
        time_estimate_minutes: this.parseTimeStringToMinutes(this.time_estimate),
      }

      axios.put(url, data, config)
        .then(response => {
          this.modals.time_tracking = false
          this.$notify({ type: 'success', message: 'Time estimate updated', timeout: 2000 })
          this.getTicket(true)
        })
        .catch(err => {
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          }
        })
    },
    async updateTicket(){
      if(!this.validateTicket()){
        return
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' +
        this.companyGroupId + '/projects/' + this.projectId + '/tickets/' + this.ticketId
      const data = {
        title: this.title,
        description: this.savedDescriptionContent || '',
        priority: this.priority.id,
        status: this.status.id,
        assignee: this.assignee.id,
        type_id: this.ticket_type ? this.ticket_type.id : null,
        due_date: this.due_date
      }

      axios.put(url, data, config)
        .then(response => {
          this.loading = false
          this.showAlert('Success', 'Ticket has been updated', 'success')
          this.getTicket(true)
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          }
        })
    },
    // --- Attachment methods ---

    async loadAttachments() {
      if (this.attachmentsLoaded) return
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}/attachments`
        const response = await axios.get(url, config)
        this.attachments = response.data
        this.attachmentsLoaded = true
      } catch (err) {
        console.log(err)
        this.$notify({ type: 'danger', message: 'Failed to load attachments' })
      }
    },

    handleDrop(e) {
      this.isDragging = false
      const files = e.dataTransfer.files
      if (files.length) this.uploadFiles(files)
    },

    handleFileSelect(e) {
      const files = e.target.files
      if (files.length) this.uploadFiles(files)
      e.target.value = ''
    },

    isHeicFile(file) {
      if (file.type === 'image/heic' || file.type === 'image/heif') return true
      const name = (file.name || '').toLowerCase()
      return name.endsWith('.heic') || name.endsWith('.heif')
    },

    async putToS3WithRetry(uploadUrl, file, entry) {
      const buildConfig = () => ({
        headers: { 'Content-Type': file.type },
        onUploadProgress: (e) => {
          if (e.total) entry.progress = Math.round((e.loaded / e.total) * 100)
        }
      })
      try {
        return await axios.put(uploadUrl, file, buildConfig())
      } catch (err) {
        // Retry once on network/connection errors only (no HTTP response received)
        if (err.response) throw err
        entry.progress = 0
        entry.retrying = true
        return await axios.put(uploadUrl, file, buildConfig())
      }
    },

    async uploadFiles(files) {
      const ALLOWED_TYPES = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/csv', 'text/plain',
        'application/zip',
      ]
      const MAX_SIZE = 25 * 1024 * 1024

      for (const file of files) {
        if (this.isHeicFile(file)) {
          this.uploadErrors.push(`${file.name} — HEIC photos aren't supported. On iPhone, go to Settings → Camera → Formats and select "Most Compatible", or save the photo as JPEG before uploading.`)
          continue
        }
        if (!ALLOWED_TYPES.includes(file.type)) {
          this.uploadErrors.push(`${file.name} — file type not supported`)
          continue
        }
        if (file.size > MAX_SIZE) {
          this.uploadErrors.push(`${file.name} — exceeds 25 MB limit`)
          continue
        }

        const uploadEntry = { name: file.name, progress: null, retrying: false }
        this.uploadingFiles.push(uploadEntry)

        try {
          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }

          // Get presigned URL
          const uploadRes = await axios.post(`${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}/attachments`, {
            file_name: file.name,
            content_type: file.type,
            file_size: file.size
          }, config)

          const { upload_url } = uploadRes.data

          // Upload directly to S3 (with progress + one network retry)
          await this.putToS3WithRetry(upload_url, file, uploadEntry)

          // Refresh attachment list
          this.attachmentsLoaded = false
          await this.loadAttachments()
          this.$notify({ type: 'success', message: 'File uploaded', timeout: 2000 })
        } catch (err) {
          console.log(err)
          this.uploadErrors.push(`${file.name} — upload failed`)
        } finally {
          const idx = this.uploadingFiles.indexOf(uploadEntry)
          if (idx > -1) this.uploadingFiles.splice(idx, 1)
        }
      }
    },

    async downloadAttachment(att) {
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}/attachments/${att.id}`
        const response = await axios.get(url, config)
        const { download_url } = response.data

        // Trigger download
        const a = document.createElement('a')
        a.href = download_url
        a.download = att.file_name
        a.target = '_blank'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (err) {
        console.log(err)
        this.$notify({ type: 'danger', message: 'Failed to download file' })
      }
    },

    async previewAttachment(att) {
      if (this.isImage(att.content_type)) {
        try {
          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}/attachments/${att.id}`
          const response = await axios.get(url, config)
          this.lightboxImage = response.data.download_url
        } catch (err) {
          console.log(err)
        }
      } else if (att.content_type === 'application/pdf' || att.content_type === 'text/plain') {
        this.downloadAttachment(att)
      }
    },

    async confirmDeleteAttachment(att) {
      const ok = await this.askConfirm('Delete this attachment?', 'This attachment will be permanently deleted.', 'Delete')
      if (ok) this.deleteAttachment(att)
    },

    async deleteAttachment(att) {
      this.deletingAttachmentId = att.id
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}/attachments/${att.id}`
        await axios.delete(url, config)
        this.attachments = this.attachments.filter(a => a.id !== att.id)
        this.$notify({ type: 'success', message: 'Attachment deleted', timeout: 2000 })
      } catch (err) {
        console.log(err)
        if (err.response?.status === 403) {
          this.$notify({ type: 'danger', message: 'Not authorized to delete this attachment' })
        } else {
          this.$notify({ type: 'danger', message: 'Failed to delete attachment' })
        }
      } finally {
        this.deletingAttachmentId = null
      }
    },

    isImage(contentType) {
      return contentType && contentType.startsWith('image/')
    },

    isSpreadsheet(contentType) {
      return contentType && (
        contentType.includes('spreadsheet') ||
        contentType.includes('excel') ||
        contentType === 'text/csv'
      )
    },

    isDocument(contentType) {
      return contentType && (
        contentType.includes('word') ||
        contentType.includes('presentation') ||
        contentType.includes('powerpoint')
      )
    },

    getExtension(fileName) {
      if (!fileName || !fileName.includes('.')) return ''
      return fileName.split('.').pop().toUpperCase()
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    async getTicket(silent){
      if (!silent) this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' +
        this.companyGroupId + '/projects/' + this.projectId + '/tickets/' + this.ticketId + '?progressive=true'

      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.ticket = response.data.ticket
          this.statuses = response.data.statuses
          this.ticket_types = response.data.ticket_types
          this.users = response.data.users
          this.comments = response.data.comments
          this.time_logs = response.data.time_logs || []
          this.watchers = response.data.watchers || []
          this.subtasks = response.data.subtasks || []
          this.parentTicket = response.data.parent_ticket || null
          this.title = this.ticket.title
          this.originalTitle = this.ticket.title
          this.priority = _.find(this.priorities, {id: this.ticket.priority}) || {id: this.ticket.priority}
          this.status = _.find(this.statuses, {id: this.ticket.status})
          this.assignee = {id: this.ticket.assignee_id}
          this.due_date = this.ticket.due_date ? this.ticket.due_date.substring(0, 10) : null
          this.ticket_type = _.find(this.ticket_types, {id: this.ticket.ticket_type_id}) || null
          if (this.ticket_type) this.ticket_type.archived = 0
          this.categories_list = response.data.categories.filter(c => c.archived === 0);
          this.savedDescriptionContent = this.ticket.description
          this.getPercentCompleted()
          this.applyStatusColor()
          this.loadAllComments()
          this.loadAttachments()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response?.status == 401){
            this.$router.push('/projects?count=10&page=1')
          }
        })
    },
    formatSubtaskDueDate(d){
      if (!d) return ''
      const dateStr = d.substring(0, 10)
      const m = moment(dateStr, 'YYYY-MM-DD')
      return m.format(this.date_format)
    },
    isSubtaskOverdue(subtask){
      if (!subtask.due_date) return false
      const dueStr = subtask.due_date.substring(0, 10)
      const today = new Date().toISOString().substring(0, 10)
      if (this.isSubtaskCompleted(subtask)) return false
      return dueStr < today
    },
    startEditTitle(){
      this.originalTitle = this.title
      this.editingTitle = true
      this.$nextTick(() => {
        if (this.$refs.titleInput) this.$refs.titleInput.focus()
      })
    },
    getLocalDateTime(d){
      if(!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if(!date && !time) return ''
      return `${date}, ${time}`
    },
    getPriority(priority){
      let p = _.find(this.priorities, {id: priority})
      return p
    },
    navigateToSubtask(subtask) {
      this.$router.push('/projects/' + this.projectId + '/tickets/' + subtask.id)
    },
    isSubtaskCompleted(subtask) {
      if (subtask.lifecycle_status) return subtask.lifecycle_status === 'done'
      const status = this.statuses.find(s => s.id === subtask.status)
      return status ? (status.lifecycle_status === 'done' || status.name === 'Done') : false
    },
    async toggleSubtaskComplete(subtask) {
      // Statuses arrive sorted by name, so pick by lifecycle and lowest id
      // (Created to reopen, Done to complete) instead of list order.
      const lowestId = (predicate) => this.statuses.filter(predicate).sort((a, b) => a.id - b.id)[0]
      const targetStatus = this.isSubtaskCompleted(subtask)
        ? (lowestId(s => s.lifecycle_status === 'not_started') || this.statuses.find(s => s.name === 'Created'))
        : (lowestId(s => s.lifecycle_status === 'done') || this.statuses.find(s => s.name === 'Done'))

      if (!targetStatus) return

      const prevStatus = subtask.status
      const prevStatusName = subtask.status_name
      const prevLabelColor = subtask.label_color
      const prevLifecycle = subtask.lifecycle_status

      this.$set(subtask, 'status', targetStatus.id)
      this.$set(subtask, 'status_name', targetStatus.name)
      this.$set(subtask, 'label_color', targetStatus.label_color)
      this.$set(subtask, 'lifecycle_status', targetStatus.lifecycle_status)

      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${subtask.id}`
        await axios.put(url, {
          title: subtask.title || '',
          priority: subtask.priority,
          status: targetStatus.id,
          assignee: subtask.assignee || null
        }, config)
      } catch (err) {
        this.$set(subtask, 'status', prevStatus)
        this.$set(subtask, 'status_name', prevStatusName)
        this.$set(subtask, 'label_color', prevLabelColor)
        this.$set(subtask, 'lifecycle_status', prevLifecycle)
        this.$notify({ type: 'danger', message: 'Failed to update subtask status' })
      }
    },
    openCreateSubtaskForm() {
      this.editingSubtaskId = null
      this.showCreateSubtaskForm = true
      this.newSubtask = { title: '', priority: 3, status: 1, assignee: null }
      this.$nextTick(() => {
        if (this.$refs.createSubtaskTitle) {
          this.$refs.createSubtaskTitle.focus()
        }
      })
    },
    cancelCreateSubtask() {
      this.showCreateSubtaskForm = false
      this.newSubtask = { title: '', priority: 3, status: 1, assignee: null }
    },
    async createSubtask() {
      if (!this.newSubtask.title.trim()) return

      const defaultType = this.ticket_types.find(t => t.archived === 0)

      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets`

        const data = {
          title: this.newSubtask.title.trim(),
          description: '',
          ticket_type: defaultType ? defaultType.id : this.ticket_type.id,
          assignee: this.newSubtask.assignee,
          status: this.newSubtask.status,
          priority: this.newSubtask.priority,
          due_date: this.newSubtask.due_date,
          parent_ticket_id: Number(this.ticketId)
        }

        const response = await axios.post(url, data, config)
        const created = typeof response.data === 'string' ? JSON.parse(response.data) : response.data

        const selectedStatus = this.statuses.find(s => s.id === this.newSubtask.status) || this.statuses[0]
        const selectedUser = this.newSubtask.assignee ? this.users.find(u => u.id === this.newSubtask.assignee) : null

        this.subtasks.push({
          id: created.id,
          ticket_key: this.ticket.ticket_key.substring(0, this.ticket.ticket_key.lastIndexOf('-')) + '-' + created.identifier,
          title: data.title,
          status: selectedStatus.id,
          status_name: selectedStatus.name,
          label_color: selectedStatus.label_color,
          lifecycle_status: selectedStatus.lifecycle_status,
          priority: this.newSubtask.priority,
          assignee: this.newSubtask.assignee,
          assignee_name: selectedUser ? selectedUser.name : null,
          assignee_first_name: selectedUser ? selectedUser.first_name : null,
          assignee_last_name: selectedUser ? selectedUser.last_name : null,
          due_date: this.newSubtask.due_date
        })

        this.newSubtask = { title: '', priority: 3, status: 1, assignee: null, due_date: null }
        this.showCreateSubtaskForm = false
        this.$notify({ type: 'success', message: 'Subtask created', timeout: 2000 })
      } catch (err) {
        console.log(err)
        this.$notify({ type: 'danger', message: 'Failed to create subtask' })
      }
    },
    startEditSubtask(subtask) {
      this.showCreateSubtaskForm = false
      this.editingSubtaskId = subtask.id
      this.editSubtask = {
        title: subtask.title,
        priority: subtask.priority,
        status: subtask.status,
        assignee: subtask.assignee || null,
        due_date: subtask.due_date ? subtask.due_date.substring(0, 10) : null
      }
      this.$nextTick(() => {
        if (this.$refs.editSubtaskTitle && this.$refs.editSubtaskTitle[0]) {
          this.$refs.editSubtaskTitle[0].focus()
        }
      })
    },
    cancelEditSubtask() {
      this.editingSubtaskId = null
      this.editSubtask = { title: '', priority: null, status: null, assignee: null, due_date: null }
    },
    async saveEditSubtask(subtask) {
      if (!this.editSubtask.title.trim()) return

      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${subtask.id}`

        await axios.put(url, {
          title: this.editSubtask.title.trim(),
          priority: this.editSubtask.priority,
          status: this.editSubtask.status,
          assignee: this.editSubtask.assignee,
          due_date: this.editSubtask.due_date
        }, config)

        const selectedStatus = this.statuses.find(s => s.id === this.editSubtask.status)
        const selectedUser = this.editSubtask.assignee ? this.users.find(u => u.id === this.editSubtask.assignee) : null

        this.$set(subtask, 'title', this.editSubtask.title.trim())
        this.$set(subtask, 'priority', this.editSubtask.priority)
        this.$set(subtask, 'status', this.editSubtask.status)
        this.$set(subtask, 'status_name', selectedStatus ? selectedStatus.name : subtask.status_name)
        this.$set(subtask, 'label_color', selectedStatus ? selectedStatus.label_color : subtask.label_color)
        this.$set(subtask, 'lifecycle_status', selectedStatus ? selectedStatus.lifecycle_status : subtask.lifecycle_status)
        this.$set(subtask, 'assignee', this.editSubtask.assignee)
        this.$set(subtask, 'assignee_name', selectedUser ? selectedUser.name : null)
        this.$set(subtask, 'assignee_first_name', selectedUser ? selectedUser.first_name : null)
        this.$set(subtask, 'assignee_last_name', selectedUser ? selectedUser.last_name : null)
        this.$set(subtask, 'due_date', this.editSubtask.due_date)

        this.editingSubtaskId = null
        this.$notify({ type: 'success', message: 'Subtask updated', timeout: 2000 })
      } catch (err) {
        console.log(err)
        this.$notify({ type: 'danger', message: 'Failed to update subtask' })
      }
    },
    async deleteSubtask(subtask) {
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${subtask.id}`

        await axios.delete(url, config)

        const idx = this.subtasks.findIndex(s => s.id === subtask.id)
        if (idx > -1) this.subtasks.splice(idx, 1)
        this.$notify({ type: 'success', message: 'Subtask deleted', timeout: 2000 })
      } catch (err) {
        console.log(err)
        this.$notify({ type: 'danger', message: 'Failed to delete subtask' })
      }
    },
    async onSubtaskDragEnd() {
      const previousOrder = [...this.subtasks]
      const subtaskIds = this.subtasks.map(s => s.id)

      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/projects/${this.projectId}/tickets/${this.ticketId}`
        await axios.put(url, { subtask_positions: subtaskIds }, config)
      } catch (err) {
        console.log(err)
        this.subtasks = previousOrder
        this.$notify({ type: 'danger', message: 'Could not save the new order. The list has been restored.' })
      }
    },
    handleSubtaskKeydown(event, index) {
      if (event.altKey && event.key === 'ArrowUp' && index > 0) {
        event.preventDefault()
        this.moveSubtask(index, index - 1)
      } else if (event.altKey && event.key === 'ArrowDown' && index < this.subtasks.length - 1) {
        event.preventDefault()
        this.moveSubtask(index, index + 1)
      }
    },
    moveSubtask(fromIndex, toIndex) {
      const item = this.subtasks.splice(fromIndex, 1)[0]
      this.subtasks.splice(toIndex, 0, item)
      this.reorderAnnouncement = `${item.ticket_key || item.title} moved to position ${toIndex + 1} of ${this.subtasks.length}`
      this.$nextTick(() => {
        if (this.$refs.dragHandles && this.$refs.dragHandles[toIndex]) {
          this.$refs.dragHandles[toIndex].focus()
        }
      })
      this.onSubtaskDragEnd()
    },
  },
  mounted(){
    this.dateFormatCal = this.date_format.replace(/Y/g, 'y').replace(/D/g, 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    if (this.time_format === 'HH:mm') {
      this.picker_time_format = 'HH:mm'
    }
    this.projectId = this.$route.params.id
    this.ticketId = this.$route.params.ticket_id
    this.getTicket()
  }
}
</script>

<style scoped>
/* ─── Title (click-to-edit) ─── */
.ticket-title-display {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
  border-radius: 4px;
}
.ticket-title-display .card-title {
  font-size: 20px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: -0.2px;
}
.ticket-title-display:hover {
  background: rgba(255, 255, 255, 0.03);
}
.ticket-title-pencil {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
  transition: color 0.15s ease;
}
.ticket-title-display:hover .ticket-title-pencil {
  color: #e14eca;
}
.ticket-title-input {
  font-size: 20px;
  font-weight: 600;
  color: white;
  background: rgba(30, 30, 47, 0.4);
  border: 1px solid #e14eca;
  padding: 4px 8px;
  line-height: 1.3;
  letter-spacing: -0.2px;
}
.ticket-title-input:focus {
  outline: none;
  box-shadow: none;
  border-color: #e14eca;
  background: rgba(30, 30, 47, 0.4);
}

/* ─── Section labels ─── */
.section-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
}

/* ─── Pill tab navigation ─── */

/* ─── Empty state ─── */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}
.empty-state i {
  font-size: 2.5em;
  color: rgba(255, 255, 255, 0.15);
}
.empty-state p {
  margin-top: 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

/* ─── Parent ticket banner ─── */
.parent-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  margin-bottom: 8px;
  background: rgba(29, 140, 248, 0.08);
  border-radius: 4px;
  border-left: 3px solid #1d8cf8;
}
.parent-banner i {
  font-size: 12px;
  color: #1d8cf8;
}
.parent-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}
.parent-link {
  font-size: 12px;
  color: #1d8cf8;
  text-decoration: none;
}
.parent-link:hover {
  text-decoration: underline;
}

/* ─── Subtask progress ─── */
.subtask-progress {
  margin-bottom: 12px;
}
.subtask-progress-header {
  margin-bottom: 6px;
}
.subtask-progress-text {
  font-size: 12px;
  color: rgba(125, 125, 125, 0.8);
}
.subtask-progress-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  overflow: hidden;
}
.subtask-progress-fill {
  height: 100%;
  background: #e14eca;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* ─── Subtask drag handle ─── */
.subtask-drag-handle {
  font-size: 12px;
  cursor: move;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.35);
  transition: color 0.15s;
  padding: 4px;
}
.subtask-drag-handle:hover {
  color: rgba(255, 255, 255, 0.7);
}
.subtask-drag-handle:active {
  color: rgba(255, 255, 255, 1);
}
.subtask-drag-handle--mobile {
  font-size: 11px;
  padding: 6px 4px;
}
.subtask-ghost {
  opacity: 0.5;
  background: rgba(225, 78, 202, 0.08);
  border-left: 2px solid #e14eca;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.subtask-chosen {
  background: rgba(225, 78, 202, 0.06);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

/* ─── Subtask rows ─── */
.subtask-row {
  transition: background-color 0.15s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}
.subtask-row:hover {
  background-color: rgba(125, 125, 125, 0.08);
}
.subtask-title {
  transition: opacity 0.2s ease;
}
.subtask-link {
  cursor: pointer;
  color: #e14eca !important;
  &:hover {
    opacity: 0.8 !important;
    text-decoration: none !important;
  }
}
.subtask-actions {
  opacity: 0;
  transition: opacity 0.15s ease;
}
.subtask-row:hover .subtask-actions {
  opacity: 1;
}
.subtask-actions i:hover {
  color: #e14eca !important;
}
.subtask-cb {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  border: 1px solid rgba(125, 125, 125, 0.4);
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  transition: all 0.15s ease;
  margin: 0;
  padding: 0;
}
.subtask-cb:checked {
  background-color: #e14eca;
  border-color: #e14eca;
}
.subtask-cb:checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(45deg);
}
.subtask-inline-select .el-input__inner {
  height: calc(2.25rem + 2px);
  background-color: inherit;
  border-color: rgba(29, 37, 59, 0.5);
  color: white;
}

/* ─── Sidebar status row (promoted, top of card) ─── */
.sidebar-status-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 16px;
}
::v-deep(.sidebar-status-select .el-input__inner) {
  border: none !important;
  color: #fff !important;
  padding: 4px 24px 4px 10px !important;
  height: 28px !important;
  line-height: 20px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 4px !important;
}
::v-deep(.sidebar-status-select .el-input__suffix) {
  right: 4px;
}
::v-deep(.sidebar-status-select .el-select__caret) {
  color: rgba(255, 255, 255, 0.6) !important;
  font-size: 11px !important;
}
::v-deep(.sidebar-status-select .el-input.is-focus .el-input__inner) {
  border: none !important;
}
::v-deep(.sidebar-status-select .el-select-dropdown__wrap) {
  max-height: none !important;
}
::v-deep(.sidebar-status-select .el-select-dropdown__item.hover),
::v-deep(.sidebar-status-select .el-select-dropdown__item:hover),
::v-deep(.sidebar-status-select .select-primary.el-select-dropdown__item.hover),
::v-deep(.sidebar-status-select .select-primary.el-select-dropdown__item:hover) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}
::v-deep(.sidebar-status-select .el-select-dropdown__item.selected),
::v-deep(.sidebar-status-select .el-select-dropdown__item.selected.hover),
::v-deep(.sidebar-status-select .select-primary.el-select-dropdown__item.selected),
::v-deep(.sidebar-status-select .select-primary.el-select-dropdown__item.selected.hover) {
  background-color: rgba(0, 0, 0, 0.08) !important;
}
::v-deep(.sidebar-status-select .el-select-dropdown__item.selected .badge),
::v-deep(.sidebar-status-select .select-primary.el-select-dropdown__item.selected .badge) {
  color: white !important;
}

/* ─── Saved indicator (status row) ─── */
.field-saved {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: #00f2c3;
}
.field-saved i {
  font-size: 10px;
}
.fade-saved-enter-active {
  transition: opacity 0.2s ease;
}
.fade-saved-leave-active {
  transition: opacity 0.5s ease;
}
.fade-saved-enter, .fade-saved-leave-to {
  opacity: 0;
}

/* Watchers row */
.prop-row--watchers {
  margin-top: 0;
}

/* Save feedback */
.save-feedback {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #00f2c3;
  padding: 4px 14px;
  margin-bottom: 4px;
}
.save-feedback i {
  font-size: 10px;
}
.fade-feedback-enter-active {
  transition: opacity 0.2s ease;
}
.fade-feedback-leave-active {
  transition: opacity 0.4s ease;
}
.fade-feedback-enter, .fade-feedback-leave-to {
  opacity: 0;
}

/* ─── Dark Quill editor overrides ─── */
::v-deep(.rte-dark .ql-editor) {
  background-color: #171826 !important;
  color: #ffffff !important;
}
::v-deep(.rte-dark .ql-container) {
  background-color: transparent !important;
}

/* ─── Time tracking progress bar ─── */
::v-deep(.custom-progress-bar.under-estimate .el-progress-bar__outer) {
  background-color: #163759 !important;
}
::v-deep(.custom-progress-bar.over-estimate .el-progress-bar__outer) {
  background-color: #d3102d !important;
}
::v-deep(.el-progress__text) {
  color: transparent !important;
}

/* ─── Table styles ─── */
.table-responsive {
  background-color: rgba(255,255,255,0.05);
  border-radius: 5px;
  padding: 1rem;
}
.table {
  color: white;
  margin-bottom: 0;
}
.table th {
  border-top: none;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.8);
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.table td {
  border-top: 1px solid rgba(255,255,255,0.05);
  color: white;
  vertical-align: middle;
}
.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(255,255,255,0.02);
}
.table-hover tbody tr:hover {
  background-color: rgba(255,255,255,0.05);
}

/* ─── Watcher popover ─── */
::v-deep(.watcher-popover) {
  background-color: #ffffff !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}
::v-deep(.watcher-popover .popper__arrow::after) {
  border-bottom-color: #ffffff !important;
}
.watcher-popover-content {
  color: #344675;
}
.watcher-label {
  font-weight: bold;
  font-size: 10px;
  color: #525f7f !important;
  letter-spacing: 0.5px;
}
.watcher-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding: 8px;
  background-color: #f6f9fc;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}
.watcher-name {
  font-size: 14px;
  color: #344675 !important;
  font-weight: 500;
}
.no-watchers-message {
  text-align: center;
  padding: 16px 12px;
  background-color: #f6f9fc;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}
.no-watchers-message p {
  color: #8898aa !important;
  font-size: 13px;
}

/* ─── Attachments ─── */
.attachment-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 20px;
  border: 2px dashed rgba(225, 78, 202, 0.3);
  border-radius: 8px;
  background: rgba(30, 30, 47, 0.4);
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 16px;
}
.attachment-dropzone i {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.4);
}
.attachment-dropzone span {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}
.attachment-dropzone .dropzone-hint {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
}
.attachment-dropzone:hover,
.dropzone-active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.08);
}
.attachment-dropzone:hover i,
.dropzone-active i {
  color: rgba(255, 255, 255, 0.8);
}
.attachment-dropzone:hover span,
.dropzone-active span {
  color: rgba(255, 255, 255, 0.8);
}

.attachment-uploading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(225, 78, 202, 0.08);
  border-radius: 4px;
  margin-bottom: 8px;
}
.attachment-uploading i {
  color: #e14eca;
}

.attachment-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 12px;
  color: #ec250d;
  background: rgba(236, 37, 13, 0.1);
  border-left: 3px solid #ec250d;
  border-radius: 4px;
  margin-bottom: 8px;
}

.attachment-cards {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.attachment-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  transition: background 0.15s ease;
}
.attachment-card:hover {
  background: rgba(255, 255, 255, 0.03);
}
.attachment-card:hover .attachment-actions {
  opacity: 1;
}
.attachment-deleting {
  opacity: 0.4;
  pointer-events: none;
}

.attachment-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: rgba(30, 30, 47, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  cursor: pointer;
}
.file-ext {
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
}

.attachment-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.attachment-name {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.attachment-meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}

.attachment-actions {
  display: flex;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.15s ease;
  flex-shrink: 0;
}
.attachment-actions i {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: color 0.15s ease;
}
.attachment-actions i:hover {
  color: #e14eca;
}
.attachment-actions .icon-trash-simple:hover {
  color: #ec250d;
}

/* ─── Lightbox ─── */
.attachment-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.lightbox-content {
  position: relative;
  max-width: 80vw;
  max-height: 80vh;
}
.lightbox-content img {
  max-width: 80vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
}
.lightbox-close {
  position: absolute;
  top: -30px;
  right: 0;
  font-size: 16px;
  color: white;
  cursor: pointer;
}

/* ─── Mobile ─── */
@media (max-width: 575.98px) {
  .subtask-form-actions {
    width: 100%;
    display: flex;
    gap: 8px;
  }
  .attachment-actions {
    opacity: 1;
  }
  .attachment-dropzone {
    padding: 14px;
  }
}

/* ─── Classes extracted from inline styles ─── */
.subtask-key {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
}
.enter-hint {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  pointer-events: none;
}
.subtask-due-date {
  color: rgba(255, 255, 255, 0.5);
}
.subtask-due-date.subtask-overdue {
  color: #ec250d;
}
.table-muted {
  color: rgba(255, 255, 255, 0.4);
}

/* ═══════════════════════════════════════════════════════
   LIGHT MODE OVERRIDES
   ═══════════════════════════════════════════════════════ */

/* ─── Title ─── */
.white-content .ticket-title-display .card-title {
  color: #344675;
}
.white-content .ticket-title-display:hover {
  background: rgba(0, 0, 0, 0.03);
}
.white-content .ticket-title-input {
  color: #344675;
  background: #ffffff;
}
.white-content .ticket-title-input:focus {
  background: #ffffff;
}

/* ─── Section labels ─── */
.white-content .section-label {
  color: #525f7f;
}

/* ─── Empty state ─── */
.white-content .empty-state i {
  color: rgba(0, 0, 0, 0.15);
}
.white-content .empty-state p {
  color: #9A9A9A;
}

/* ─── Parent banner ─── */
.white-content .parent-banner {
  background: rgba(29, 140, 248, 0.06);
}
.white-content .parent-label {
  color: #525f7f;
}

/* ─── Subtasks ─── */
.white-content .subtask-progress-track {
  background: rgba(0, 0, 0, 0.08);
}
.white-content .subtask-progress-text {
  color: #9A9A9A;
}
.white-content .subtask-drag-handle {
  color: rgba(0, 0, 0, 0.3);
}
.white-content .subtask-drag-handle:hover {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .subtask-drag-handle:active {
  color: rgba(0, 0, 0, 0.7);
}
.white-content .subtask-ghost {
  background: rgba(225, 78, 202, 0.06);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.white-content .subtask-row:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
.white-content .subtask-key {
  color: #9A9A9A;
}
.white-content .subtask-row {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}
.white-content .subtask-due-date {
  color: #9A9A9A;
}
.white-content .enter-hint {
  color: #9A9A9A;
}
.white-content .subtask-inline-select .el-input__inner {
  color: #344675;
}
.white-content .table-muted {
  color: #9A9A9A;
}

/* ─── Tables (time tracking) ─── */
.white-content .table-responsive {
  background-color: rgba(0, 0, 0, 0.02);
}
.white-content .table {
  color: #344675;
}
.white-content .table th {
  border-bottom-color: #e3e3e3;
  color: #525f7f;
}
.white-content .table td {
  border-top-color: #e3e3e3;
  color: #344675;
}
.white-content .table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}
.white-content .table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Time tracking progress bar — overrides in unscoped block */

/* ─── Attachments ─── */
.white-content .attachment-dropzone {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .attachment-dropzone i {
  color: rgba(0, 0, 0, 0.3);
}
.white-content .attachment-dropzone span {
  color: #525f7f;
}
.white-content .attachment-dropzone .dropzone-hint {
  color: #9A9A9A;
}
.white-content .attachment-uploading {
  color: #525f7f;
}
.white-content .attachment-preview {
  background: #f5f6fa;
  border-color: rgba(0, 0, 0, 0.08);
}
.white-content .file-ext {
  color: #525f7f;
}
.white-content .attachment-name {
  color: #344675;
}
.white-content .attachment-meta {
  color: #9A9A9A;
}
.white-content .attachment-actions i {
  color: #525f7f;
}
.white-content .attachment-card {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}
.white-content .attachment-card:hover {
  background: rgba(0, 0, 0, 0.02);
}

/* Quill editor — overrides in unscoped block */
</style>

<style>
/* Unscoped light mode overrides for Element UI components inside ProjectTicket */
.white-content .custom-progress-bar.under-estimate .el-progress-bar__outer {
  background-color: rgba(29, 140, 248, 0.15) !important;
}
.white-content .custom-progress-bar.over-estimate .el-progress-bar__outer {
  background-color: rgba(211, 16, 45, 0.15) !important;
}
</style>
